import type { ObjectData } from '@os/doc';
import { RichText } from '../text/RichText';

type Shape = Extract<ObjectData, { type: 'shape' }>;
const STROKE = 2;

const str = (v: unknown, fallback: string) => (typeof v === 'string' ? v : fallback);
const pts = (list: [number, number][]) => list.map(([x, y]) => `${x},${y}`).join(' ');

function starPoints(w: number, h: number, i: number): string {
  const out: [number, number][] = [];
  for (let k = 0; k < 10; k++) {
    const a = -Math.PI / 2 + (k * Math.PI) / 5;
    const r = k % 2 === 0 ? 1 : 0.4;
    out.push([w / 2 + Math.cos(a) * r * (w / 2 - i), h / 2 + Math.sin(a) * r * (h / 2 - i)]);
  }
  return pts(out);
}

/** Geometry in object pixels (no viewBox), so the stroke never stretches with the box. */
export function ShapeObject({ o }: { o: Shape }) {
  const w = Math.max(o.w, 1);
  const h = Math.max(o.h, 1);
  const i = STROKE / 2;
  const style = { fill: str(o.style.fill, 'var(--canvas-fill, #ffffff)'), stroke: str(o.style.stroke, 'var(--canvas-ink, #141414)'), strokeWidth: STROKE };
  let body;
  switch (o.shape) {
    case 'rectangle':
      body = <rect x={i} y={i} width={w - STROKE} height={h - STROKE} rx={4} style={style} />;
      break;
    case 'ellipse':
      body = <ellipse cx={w / 2} cy={h / 2} rx={w / 2 - i} ry={h / 2 - i} style={style} />;
      break;
    case 'diamond':
      body = <polygon points={pts([[w / 2, i], [w - i, h / 2], [w / 2, h - i], [i, h / 2]])} style={style} />;
      break;
    case 'triangle':
      body = <polygon points={pts([[w / 2, i], [w - i, h - i], [i, h - i]])} style={style} />;
      break;
    case 'star':
      body = <polygon points={starPoints(w, h, i)} style={style} />;
      break;
    case 'bubble': {
      const b = h * 0.8;
      body = <path d={`M${i + 8},${i} H${w - i - 8} Q${w - i},${i} ${w - i},${i + 8} V${b - 8} Q${w - i},${b} ${w - i - 8},${b} H${w * 0.35} L${w * 0.2},${h - i} L${w * 0.22},${b} H${i + 8} Q${i},${b} ${i},${b - 8} V${i + 8} Q${i},${i} ${i + 8},${i} Z`} style={style} />;
      break;
    }
  }
  return (
    <>
      <svg width={w} height={h} className="block overflow-visible" aria-hidden="true">
        {body}
      </svg>
      {o.text?.trim() && (
        <div data-rich className="absolute inset-0 flex items-center justify-center overflow-hidden break-words p-2 text-center font-sans text-base text-[var(--canvas-ink,#141414)]">
          <RichText doc={o.content} fallback={o.text} />
        </div>
      )}
    </>
  );
}
